/**
 * Client home dashboard. Ported from the original app's HomeDashboard.tsx —
 * same UI verbatim, with next/link → react-router Link and the api client.
 * Reads GET /api/user/dashboard.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Dumbbell, Play, Droplets, Minus, Plus, Check, Calendar,
  TrendingUp, ChevronRight, MessageCircle, Trophy, ArrowUp, Activity, BookOpen,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { MacroTile } from '@/components/ui';

interface BlogPostSummary {
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  author: string | null;
  read_minutes: number | null;
}

interface DashboardData {
  nextWorkout: {
    name: string;
    programName: string;
    exerciseCount: number;
    estimatedMinutes: number;
    exercises: string[];
  } | null;
  nextSession: {
    date: string;
    startTime: string;
    endTime: string;
    type: string | null;
    trainer: string;
  } | null;
  todayPlan?: {
    hasWorkout: boolean;
    sessionToday: { startTime: string; endTime: string } | null;
    cardioToday: { activity: string; minutes: number | null } | null;
    needsBooking: boolean;
  };
  macros: {
    calories: number; targetCalories: number;
    protein: number; targetProtein: number;
    carbs: number; targetCarbs: number;
    fat: number; targetFat: number;
    fromHealth?: boolean;
    dayApplied?: boolean;
  } | null;
  activity: {
    source: string;
    recordedAt: string;
    steps: number | null; stepsGoal: number | null;
    activeCalories: number | null; activeCaloriesGoal: number | null;
    exerciseMinutes: number | null; standMinutes: number | null;
    distanceMiles: number | null; flightsClimbed: number | null;
    sleepHours: number | null; sleepGoal: number | null;
    restingHeartRate: number | null;
  } | null;
  workoutCount: number;
  streak: number;
  recentPR: { exerciseName: string; weight: number; reps: number; improvement: number | null } | null;
  weightChange: string | null;
  prCount: number;
}

export default function HomeDashboard() {
  const { user } = useAuth();
  const avatarUrl = user?.avatar_url ?? null;

  // A REAL personal name (display_name or first name), or null — never the email.
  // Greeting reads "Good afternoon, Vickie" when set, just "Good afternoon" when not.
  const personalName =
    user?.display_name?.trim() || user?.first_name?.trim() || null;

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [waterGlasses, setWaterGlasses] = useState(0);
  const [latestPost, setLatestPost] = useState<BlogPostSummary | null>(null);
  const waterGoal = 8;
  const greeting = getGreeting();

  useEffect(() => {
    api
      .get<DashboardData>('/api/user/dashboard')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
    api
      .get<{ posts: BlogPostSummary[] }>('/api/blog')
      .then((r) => setLatestPost(r.posts?.[0] ?? null))
      .catch(() => {});
    // Today's logged water (8 oz per glass). Persisted server-side.
    api
      .get<{ waterOz: number }>('/api/nutrition/water')
      .then((r) => setWaterGlasses(Math.round((r.waterOz ?? 0) / 8)))
      .catch(() => {});
  }, []);

  // Set the glass count and persist the absolute oz total (idempotent PUT).
  function changeWater(next: number) {
    const clamped = Math.max(0, next);
    setWaterGlasses(clamped);
    api.put('/api/nutrition/water', { waterOz: clamped * 8 }).catch(() => {});
  }

  if (loading) {
    return (
      <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] flex flex-col gap-5">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="card animate-pulse h-28" style={{ background: 'var(--surface)' }} />
        ))}
      </div>
    );
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4 flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="w-[52px] h-[52px] rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 overflow-hidden"
          style={{ background: 'var(--gold)', color: 'var(--navy)' }}
        >
          {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : (personalName ?? user?.email ?? '?')[0]?.toUpperCase()}
        </div>
        <div>
          <p className="text-lg font-semibold text-white">
            {personalName ? `${greeting}, ${personalName}` : greeting}
          </p>
        </div>
      </div>

      {/* Today — the same agenda items the Calendar shows for today: session,
          strength workout, cardio. One source of truth, surfaced at a glance. */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <Link to="/calendar" className="flex items-center gap-2" style={{ color: 'inherit', textDecoration: 'none' }}>
            <Calendar size={14} style={{ color: 'var(--gold)' }} />
            <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>Today</span>
          </Link>
          <Link to="/calendar" style={{ color: 'var(--gray-500)' }}>&rsaquo;</Link>
        </div>

        {(() => {
          const tp = data?.todayPlan;
          const nw = data?.nextWorkout;
          const hasAnything = !!(tp?.sessionToday || nw || tp?.cardioToday);
          if (!hasAnything) {
            // Nothing on today — surface the next session so booking status is clear.
            return data?.nextSession ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--gray-300)' }}>
                <Calendar size={14} style={{ color: 'var(--gold)' }} />
                <span>Rest day. Next session {fmtSessionDate(data.nextSession.date)} · {label12(data.nextSession.startTime)}</span>
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'var(--gray-400)' }}>Rest day — nothing scheduled. No session booked yet.</p>
            );
          }
          return (
            <div className="flex flex-col gap-2.5">
              {tp?.sessionToday && (
                <div className="flex items-center gap-2 text-sm text-white">
                  <Calendar size={14} style={{ color: 'var(--gold)' }} />
                  <span>Session with your coach · {label12(tp.sessionToday.startTime)}</span>
                </div>
              )}
              {nw && (
                <div className="flex items-start gap-2 text-sm">
                  <Dumbbell size={14} style={{ color: 'var(--gold)', marginTop: 2 }} />
                  <div>
                    <span className="text-white font-medium">{nw.name}</span>
                    <span style={{ color: 'var(--gray-400)' }}> · {nw.exerciseCount} exercises</span>
                  </div>
                </div>
              )}
              {tp?.cardioToday && (
                <div className="flex items-center gap-2 text-sm text-white">
                  <Activity size={14} style={{ color: 'var(--gold)' }} />
                  <span>{tp.cardioToday.activity}{tp.cardioToday.minutes ? ` · ${tp.cardioToday.minutes} min` : ''}</span>
                </div>
              )}
              {/* CTA: book if strength is on the plan but no session; else start. */}
              {tp?.needsBooking ? (
                <Link to="/calendar" className="btn-gold w-full mt-1 flex items-center justify-center gap-2">
                  <Calendar size={16} /> Book your session
                </Link>
              ) : nw ? (
                <Link to="/workouts/log" className="btn-gold w-full mt-1 flex items-center justify-center gap-2">
                  <Play size={16} fill="currentColor" /> Start Workout
                </Link>
              ) : null}
            </div>
          );
        })()}
      </div>

      {/* Macros — tap through to the full Nutrition screen. It's no longer a
          bottom tab, so this card is the entry point (mirrors the native apps). */}
      <Link to="/nutrition" className="card block" style={{ color: 'inherit', textDecoration: 'none' }}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
            Today&apos;s Nutrition
          </p>
          <div className="flex items-center gap-2">
            {data?.macros?.dayApplied && (
              <span className="text-[9px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(201,168,106,0.15)', color: 'var(--champagne)', border: '1px solid var(--gold-muted)' }}>
                Day plan
              </span>
            )}
            <span style={{ color: 'var(--gray-500)' }}>&rsaquo;</span>
          </div>
        </div>
        {/* Four equal rounded-square tiles, matching the native apps. */}
        {data?.macros ? (
          <div className="grid grid-cols-4 gap-2">
            <MacroTile value={data.macros.calories} target={data.macros.targetCalories} label="Calories" color="var(--gold)" />
            <MacroTile value={data.macros.protein} target={data.macros.targetProtein} label="Protein" color="var(--success)" />
            <MacroTile value={data.macros.carbs} target={data.macros.targetCarbs} label="Carbs" color="var(--info)" />
            <MacroTile value={data.macros.fat} target={data.macros.targetFat} label="Fat" color="var(--warning)" />
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            <MacroTile value={0} target={2000} label="Calories" color="var(--gold)" />
            <MacroTile value={0} target={150} label="Protein" color="var(--success)" />
            <MacroTile value={0} target={250} label="Carbs" color="var(--info)" />
            <MacroTile value={0} target={65} label="Fat" color="var(--warning)" />
          </div>
        )}
      </Link>

      {/* Today's Activity (from Apple Health / Health Connect) */}
      {data?.activity && hasActivity(data.activity) && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity size={14} style={{ color: 'var(--success)' }} />
              <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
                Today&apos;s Activity
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--gray-500)' }}>
              {healthLabel(data.activity.source)}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {/* Steps — full-width rounded rectangle. */}
            {data.activity.steps != null && (
              <MacroTile value={data.activity.steps} target={data.activity.stepsGoal || 10000} label="steps" color="var(--success)" wide />
            )}
            {/* Remaining activity metrics — equal squares. */}
            <div className="grid grid-cols-3 gap-2">
              {data.activity.activeCalories != null && (
                <MacroTile value={data.activity.activeCalories} target={data.activity.activeCaloriesGoal || 500} label="cal" color="var(--gold)" />
              )}
              {data.activity.exerciseMinutes != null && (
                <MacroTile value={data.activity.exerciseMinutes} target={30} label="min" color="var(--info)" />
              )}
              {data.activity.sleepHours != null && (
                <MacroTile value={data.activity.sleepHours} target={data.activity.sleepGoal || 8} label="hrs" color="#818cf8" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Water */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Droplets size={14} style={{ color: 'var(--water)' }} />
            <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--water)' }}>
              Water Intake
            </span>
          </div>
          <span className="text-xs" style={{ color: 'var(--gray-400)' }}>
            {waterGlasses * 8} / {waterGoal * 8} oz
          </span>
        </div>
        <div className="w-full h-2 rounded-full mb-3" style={{ background: 'var(--navy)' }}>
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${Math.min((waterGlasses / waterGoal) * 100, 100)}%`, background: 'var(--water)' }}
          />
        </div>
        <div className="flex justify-center gap-1.5 mb-3">
          {Array.from({ length: waterGoal }, (_, i) => (
            <Droplets key={i} size={18} fill={i < waterGlasses ? 'var(--water)' : 'none'} style={{ color: i < waterGlasses ? 'var(--water)' : 'var(--navy-light)' }} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeWater(waterGlasses - 1)}
            className="w-10 h-10 rounded-lg flex items-center justify-center border transition-colors"
            style={{ borderColor: 'var(--navy-light)', color: waterGlasses === 0 ? 'var(--gray-600)' : 'var(--gray-300)' }}
            disabled={waterGlasses === 0}
            aria-label="Remove glass"
          >
            <Minus size={16} />
          </button>
          {waterGlasses >= waterGoal ? (
            <div className="flex-1 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--success)' }}>
              <Check size={20} strokeWidth={2.5} />
            </div>
          ) : (
            <button
              onClick={() => changeWater(waterGlasses + 1)}
              className="flex-1 h-10 rounded-lg flex items-center justify-center gap-2 border transition-colors"
              style={{ borderColor: 'var(--info-dark)', background: 'rgba(56,189,248,0.08)', color: 'var(--water)' }}
            >
              <Plus size={16} />
              <span className="text-sm font-medium">Add Glass</span>
            </button>
          )}
        </div>
      </div>

      {/* Upcoming session (or book-a-session prompt) */}
      <Link to="/home/appointments" className="card flex items-center gap-3 cursor-pointer transition-all hover:border-[var(--gold-muted)]">
        <Calendar size={20} style={{ color: 'var(--gold)' }} />
        <div className="flex-1">
          <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
            {data?.nextSession ? 'Next Session' : 'Book a Session'}
          </span>
          {data?.nextSession ? (
            <>
              <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>
                {fmtSessionDate(data.nextSession.date)} · {data.nextSession.startTime}–{data.nextSession.endTime}
              </p>
              <p className="text-xs" style={{ color: 'var(--gray-400)' }}>
                {data.nextSession.type ? `${data.nextSession.type} · ` : ''}{data.nextSession.trainer}
              </p>
            </>
          ) : (
            <p className="text-sm" style={{ color: 'var(--gray-300)' }}>Schedule time with your coach</p>
          )}
        </div>
        <ChevronRight size={18} style={{ color: 'var(--gray-500)' }} />
      </Link>

      {/* Progress */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} style={{ color: 'var(--gold)' }} />
            <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
              Progress
            </span>
          </div>
          <Link to="/home/progress" className="flex items-center gap-1 text-xs" style={{ color: 'var(--gray-500)' }}>
            View All <ChevronRight size={12} />
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-0">
          <div className="text-center">
            <p className="text-2xl" style={{ fontFamily: "var(--font-display), Georgia, serif", lineHeight: 1.05, color: "var(--cream)" }}>{data?.workoutCount || 0}</p>
            <p className="text-xs" style={{ color: 'var(--gray-400)' }}>Workouts</p>
          </div>
          <div className="text-center border-x" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            <p className="text-2xl font-bold" style={{ color: 'var(--success)' }}>{data?.weightChange ?? '--'}{data?.weightChange ? ' lbs' : ''}</p>
            <p className="text-xs" style={{ color: 'var(--gray-400)' }}>Wt Change</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold" style={{ color: 'var(--gold)' }}>{data?.prCount || 0}</p>
            <p className="text-xs" style={{ color: 'var(--gray-400)' }}>New PRs</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <Link to="/profile/messages" className="card flex items-start gap-3 cursor-pointer transition-all hover:border-[var(--gold-muted)]">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ background: 'var(--gold)', color: 'var(--navy)' }}>
          <MessageCircle size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white">Messages</p>
          <p className="text-sm mt-1" style={{ color: 'var(--gray-400)' }}>Chat with your coach</p>
        </div>
        <span style={{ color: 'var(--gray-500)' }}>&rsaquo;</span>
      </Link>

      {/* Recent PR */}
      {data?.recentPR && (
        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={14} style={{ color: 'var(--gold)' }} />
            <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
              Recent PR
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-white">{data.recentPR.exerciseName}</p>
              <p className="text-sm" style={{ color: 'var(--gray-400)' }}>
                {data.recentPR.weight} lbs &times; {data.recentPR.reps} reps
              </p>
            </div>
            {data.recentPR.improvement && data.recentPR.improvement > 0 && (
              <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full" style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--success)' }}>
                <ArrowUp size={12} />
                <span className="text-xs font-medium">+{data.recentPR.improvement} lbs</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Latest from the Journal — tap the card to read it, "View all" → list. */}
      {latestPost && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
              <BookOpen size={13} className="inline mr-1.5 -mt-0.5" />
              Latest from the Journal
            </span>
            <Link to="/community" className="text-xs" style={{ color: 'var(--gold)' }}>View all</Link>
          </div>
          <Link to={`/community/${latestPost.slug}`} className="card block">
            {latestPost.category && (
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--gold)' }}>
                {latestPost.category}
              </p>
            )}
            <p className="text-lg font-semibold text-white leading-snug">{latestPost.title}</p>
            {latestPost.excerpt && (
              <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--gray-400)' }}>{latestPost.excerpt}</p>
            )}
            <p className="text-xs mt-2" style={{ color: 'var(--gray-500)' }}>
              {[latestPost.author, latestPost.read_minutes ? `${latestPost.read_minutes} min read` : null].filter(Boolean).join(' · ')}
            </p>
          </Link>
        </div>
      )}
    </div>
  );
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function healthLabel(source?: string): string {
  if (source === 'apple_health') return 'Apple Health';
  if (source === 'health_connect') return 'Health Connect';
  return 'Health';
}

function hasActivity(a: NonNullable<DashboardData['activity']>): boolean {
  return [a.steps, a.activeCalories, a.exerciseMinutes, a.sleepHours].some((v) => v != null);
}

// "Fri, Jun 5" from a YYYY-MM-DD date, in local terms (parse as local, not UTC).
function fmtSessionDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** "10:30" -> "10:30 AM". */
function label12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// MacroRing (circular) was replaced by the shared MacroTile (components/ui.tsx)
// to match the native apps.
